import { IUserPatchData } from "Auth/types";
import {
  IFile,
  IPermission,
  IPermissionForKey,
  IPhone,
  IPhoneNumber,
  IPlace,
} from "shared/types";
import { IBand, IBaseBandSetting } from "./general";

interface IBandUserUser extends IUserPatchData {
  id: string;
}

export interface IBandUserProps {
  nickname?: string;
  function?: string;
  category?: string;
}

export interface IBandUser {
  id: string;
  nickname?: string;
  function?: string;
  band?: string;
  category?: {
    id: string;
  };
  user?: IBandUserUser;
  invitation?: IBandInvitation;
  email: string;
  firstname?: string;
  lastname?: string;
  birthDate?: string;
  addresses?: IPlace[];
  phoneNumbers?: IPhoneNumber[];
  permissions?: IPermissionForKey[];
}

export enum UserBooleanAttribute {
  yes = "yes",
  no = "no",
}

export enum UserShirtSize {
  xxs = "xxs",
  xs = "xs",
  s = "s",
  m = "m",
  l = "l",
  xl = "xl",
  xxl = "xxl",
  xxxl = "xxxl",
}

export interface IBandUserBodyPreferencesProps {
  smoker?: boolean | UserBooleanAttribute;
  shirtSize?: UserShirtSize;
  shoeSize?: string;
  allergies?: string;
  dietaryRequirements?: string;
  otherImportantInformation?: string;
}

export interface IBandUserBodyPreferences {
  bodyPreferences?: IBandUserBodyPreferencesProps;
}

export enum UserRoomLocation {
  none = "no_preference",
  highFloor = "high_floor",
  lowFloor = "low_floor",
}

export enum UserElevatorProximity {
  none = "no_preference",
  nearElevator = "near_elevator",
  farFromElevator = "far_from_elevator",
}

export interface IBandUserHotelPreferencesProps {
  smokingRoom?: boolean | UserBooleanAttribute;
  accessibleRoom?: boolean | UserBooleanAttribute;
  roomLocation?: UserRoomLocation | null;
  elevatorProximity?: UserElevatorProximity | null;
}

export interface IBandUserHotelPreferences {
  hotelPreferences?: IBandUserHotelPreferencesProps;
}

export enum UserSeatPreference {
  none = "no_preference",
  aisle = "aisle",
  window = "window",
}

export interface IBandUserFlightPreferencesProps {
  seatPreference?: UserSeatPreference | null;
}

export interface IBandUserFlightPreferences {
  flightPreferences?: IBandUserFlightPreferencesProps;
}

export interface IBandUserWithPermissions
  extends Omit<IBandUser, "permissions"> {
  permissions: IPermission[];
}

export interface IBandInvitationProps {
  email: string;
  firstname: string;
  lastname: string;
  nickname?: string;
  function?: string;
  category: string;
  band: string;
  signupUrl: string;
  joinUrl: string;
}

export enum InvitationStatus {
  invitation_not_sent = "invitation_not_sent",
  invitation_sent = "invitation_sent",
}

export interface IBandInvitation {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  nickname?: string;
  function?: string;
  category: IBaseBandSetting;
  band: Pick<IBand, "id" | "title">;
  status: InvitationStatus;
}

export interface IUserEmergencyContact {
  id: string;
  firstname: string;
  lastname: string;
  phoneNumber: IPhone;
  email?: string;
  note?: string;
  permissions?: IPermissionForKey[];
}

export type IUserEmergencyContactPatchData = Omit<
  IUserEmergencyContact,
  "id" | "permissions"
> & {
  user?: string;
};

export enum UserHomeBaseType {
  airport = "airport",
  trainStation = "train_station",
  carpool = "carpool",
}

export interface IUserHomeBase {
  id: string;
  name: string;
  baseType: UserHomeBaseType;
  country: string;
  city: string;
  permissions?: IPermissionForKey[];
}

export type IUserHomeBasePatchData = Omit<
  IUserHomeBase,
  "id" | "permissions"
> & {
  user?: string;
};

export enum UserPassportType {
  internationalPassport = "international_passport",
  nationalId = "national_id",
  foreignerIdCard = "foreigner_id_card",
}

export interface IUserPassport {
  id: string;
  title?: string;
  documentType: UserPassportType;
  issuingCountry: string;
  issuingAuthority?: string;
  passportNumber: string;
  issueDate: string;
  expiryDate: string;
  info?: string;
  files: IFile[];
  permissions?: IPermissionForKey[];
}

export type IUserPassportPatchData = Omit<
  IUserPassport,
  "id" | "files" | "permissions"
> & {
  user?: string;
  files?: (string | { user: string; url: string })[];
};

export interface IUserTravelVisa {
  id: string;
  title?: string;
  documentType: string;
  issuingCountry: string;
  issuingAuthority?: string;
  passportNumber: string;
  visaNumber: string;
  issueDate: string;
  expiryDate: string;
  info?: string;
  files: IFile[];
  permissions?: IPermissionForKey[];
}

export type IUserTravelVisaPatchData = Omit<
  IUserTravelVisa,
  "id" | "files" | "permissions"
> & {
  user?: string;
  files?: (string | { user: string; url: string })[];
};

export interface IUserSocialSecurityForm {
  id: string;
  title?: string;
  documentType: string;
  issuingCountry: string;
  issuingAuthority?: string;
  formNumber: string;
  issueDate: string;
  expiryDate: string;
  info?: string;
  files: IFile[];
  permissions?: IPermissionForKey[];
}

export type IUserSocialSecurityFormPatchData = Omit<
  IUserSocialSecurityForm,
  "id" | "files" | "permissions"
> & {
  user?: string;
  files?: (string | { user: string; url: string })[];
};

export enum UserDrivingPermitType {
  am = "a_m",
  a1 = "a1",
  a2 = "a2",
  a = "a",
  b = "b",
  c1 = "c1",
  c = "c",
  d1 = "d1",
  d = "d",
  be = "b_e",
  c1e = "c1_e",
  ce = "c_e",
  d1e = "d1_e",
  de = "d_e",
  g = "g",
  cherrypicker = "cherrypicker",
  forklift = "forklift",
  manitou = "manitou",
}

export interface IUserDrivingPermit {
  id: string;
  issuingCountry: string;
  issuingAuthority?: string;
  permitNumber: string;
  vehicleCategories?: UserDrivingPermitType[];
  otherVehicleCategories?: string[];
  issueDate: string;
  expiryDate?: string;
  info?: string;
  files: IFile[];
  permissions?: IPermissionForKey[];
}

export type IUserDrivingPermitPatchData = Omit<
  IUserDrivingPermit,
  "id" | "files" | "permissions"
> & {
  user?: string;
  files?: (string | { user: string; url: string })[];
};

export interface IUserVehicle {
  id: string;
  licensePlate: string;
  brand?: string;
  model?: string;
  color?: string;
  info?: string;
  permissions?: IPermissionForKey[];
}

export type IUserVehiclePatchData = Omit<IUserVehicle, "id" | "permissions"> & {
  user?: string;
};

export interface IUserLoyaltyProgram {
  id: string;
  programName: string;
  programFamily?: string;
  issuingCompany?: string;
  issueDate?: string;
  expiryDate?: string;
  membershipNumber?: string;
  membershipStatus?: string;
  permissions?: IPermissionForKey[];
}

export type IUserLoyaltyProgramPatchData = Omit<
  IUserLoyaltyProgram,
  "id" | "permissions"
> & {
  user?: string;
};
